import { isEmpty } from 'lodash'
import { startCase } from 'lodash'
import store from '../../../../../store'
import { getSectionGenerator } from './Generator'

/**
 * @param  {...boolean} predicates
 * @returns {boolean}
 */
function and(...predicates) {
  return predicates.every((it) => !!it)
}

/**
 * @param  {...boolean} predicates
 * @returns {boolean}
 */
function or(...predicates) {
  return predicates.some((it) => !!it)
}

export function generateReport() {
  const patientData = store.getters['newAttendanceForm/patientData']
  const parameters = store.getters['newAttendanceForm/parameters']
  const answers = store.getters['newAttendanceForm/answers']
  const extraAnswers = store.getters['newAttendanceForm/extraAnswers']
  const treatments = store.getters['newAttendanceForm/treatments']
  const simplified = store.getters['newAttendanceForm/simplified']
  const version = store.getters['newAttendanceForm/version']

  const input = { patientData, parameters, answers, extraAnswers, treatments, simplified }

  const generators = [
    getSectionGenerator('header', version),
    getSectionGenerator('previousTreatments', version),
    getSectionGenerator('treatmentDetails', version),
    getSectionGenerator('symptoms', version),
    getSectionGenerator('labExams', version),
    getSectionGenerator('spirometry', version),
    getSectionGenerator('footer', version),
  ]

  const sections = generators.filter((generator) => !!generator).flatMap((generator) => generator(input))

  return sections.join('\n\n')
}

/**
 * @param {string[]} texts
 * @returns {string}
 */
function simpleSection(texts) {
  return texts.join('\n')
}

/**
 * @param {string} title
 * @param {string[]} bullets
 * @returns {string}
 */
function bulletSection(title, bullets) {
  return [title, bullets.map((bullet) => `  - ${bullet}`).join('\n')].join('\n')
}

function generateHeader({ patientData, parameters }) {
  const hypothesis = parameters['asthma:hypothesis']?.label.text

  const textPieces = [
    `Paciente ${patientData.name}, ${patientData.age} anos, apresenta ${
      hypothesis?.toLocaleLowerCase() ?? '[Não disponível]'
    }`,
  ]

  if (!hypothesis || hypothesis === 'Sem diagnóstico de asma') {
    return [simpleSection(textPieces)]
  }

  const cidMap = {
    Asma: 'J45',
    'Asma grave': 'J45',
    'Asma grave eosinofílica': 'J45.1',
    'Asma grave não eosinofílica': 'J45.0',
    'Asma grave alérgica': 'J45.0',
    'Asma grave não alérgica': 'J45.1',
    'Asma grave eosinofílica alérgica': 'J45.8',
    'Asma grave eosinofílica não alérgica': 'J45.1',
    'Asma grave não eosinofílica alérgica': 'J45.0',
    'Asma grave T2 baixo (não eosinofílica e não alérgica)': 'J45.1',
  }

  textPieces.push(`(CID: ${cidMap[hypothesis] ?? '[Inválido]'})`)
  return [simpleSection(textPieces)]
}

function generatePreviousTreatments({ answers, simplified }) {
  const treatmentQuestionsKeys = ['10', '11', '13', '14', '15', '16', '19', '20']
  const filteredTreatmentQuestionKeys = treatmentQuestionsKeys.filter((key) => !isEmpty(answers[key]))

  const previous = []
  const actual = []

  for (const key of filteredTreatmentQuestionKeys) {
    for (const [medicine, answer] of Object.entries(answers[key])) {
      const title = startCase(medicine).replace(' ', ' + ')

      if (simplified) {
        switch (answer.simplePeriod) {
          case 'current':
            actual.push(title)
          case 'previous':
            previous.push(title)
        }
        continue
      }

      if (answer.fullPeriod.endDate) {
        previous.push(title)
      } else {
        actual.push(title)
      }
    }
  }

  const sections = []

  if (previous.length) {
    sections.push(bulletSection('Realizou os seguintes tratamentos prévios:', previous))
  }

  if (actual.length) {
    sections.push(bulletSection('Atualmente em uso de:', actual))
  }

  return sections
}

function generateTreatmentDetails({ answers, patientData }) {
  const textPieces = []

  if (and(patientData.age >= 6, answers['12'] === 'alta', or(!isEmpty(answers['10']), !isEmpty(answers['13'])))) {
    textPieces.push('corticoide inalatório em alta dose, com beta-2 agonista de longa duração (LABA)')
  } else if (
    and(
      patientData.age >= 6,
      patientData.age <= 11,
      answers['12'] === 'media',
      or(!isEmpty(answers['10']), !isEmpty(answers['13'])),
    )
  ) {
    textPieces.push('corticoide inalatório em média dose, com beta-2 agonista de longa duração (LABA)')
  }

  if (answers['17'] === 'sim') {
    textPieces.push('corticoide oral contínuo nos últimos 6 meses')
  }

  const sections = []

  if (textPieces.length) {
    sections.push(bulletSection('Fez uso de:', textPieces))
  }

  return sections
}

function generateSymptoms({ answers, parameters }) {
  const textPieces = []

  if (['diariamente', 'semanalmente'].includes(answers['7'])) {
    textPieces.push(`Falta de ar, sibilância, tosse ${answers['7']}`)
  }

  if (!isEmpty(answers['8']) && !['0', 'sem-informacao'].includes(answers['8'])) {
    textPieces.push(
      `${answers['8'].replace('-', ' a ').replace('+', ' ou mais')} despertares noturnos na última semana`,
    )
  }
  
  if (!isEmpty(answers['18']) && !['0', 'sem-informacao'].includes(answers['18'])) {
    textPieces.push(
      `${answers['18'].replace(
        '+',
        ' ou mais',
      )} exacerbações graves com necessidade de corticoide sistêmico no último ano`,
    )
  }

  if (!isEmpty(answers['9']) && !['0', 'sem-informacao'].includes(answers['9'])) {
    textPieces.push(
      `Precisou buscar o pronto atendimento ou foi hospitalizado ${answers['9'].replace(
        '+',
        ' ou mais',
      )} vezes no último ano, por exacerbação`,
    )
  }

  if (parameters['act']?.score <= 19) {
    textPieces.push(`ACT (Asthma Control Test) com pontuação indicando controle inadequado da asma`)
  } else if (parameters['gina']?.label === 'Asma não controlada') {
    textPieces.push(`GINA indicando controle inadequado da asma`)
  }

  const sections = []

  if (textPieces.length) {
    sections.push(bulletSection('Apresenta:', textPieces))
  }

  return sections
}

function generateLabExams({ answers }) {
  const textPieces = []

  if (['<150', 'nao-disponivel'].includes(answers['38'])) {
    textPieces.push(
      `Eosinófilos no sangue periférico: ${answers['38'].replace('-', ' a ').replace('>=', '≥')} (células/μL)`,
    )
  }

  if (answers['35'] === 'sim') {
    textPieces.push(`Prick test positivo (teste cutâneo de hipersensibilidade imediata ou teste cutâneo por puntura)`)
  }

  if (answers['36'] === 'sim') {
    textPieces.push(`IgE sérica específica positiva para pelo menos um aeroalérgeno`)
  }

  if (answers['37'] === '30-1500') {
    textPieces.push(`IgE total sérica com valor entre 30 a 1.500 UI/m`)
  }

  if (answers['39'] === 'sim') {
    textPieces.push(`FeNO (Fração exalada de óxido nítrico) aumentada`)
  }

  const sections = []

  if (textPieces.length) {
    sections.push(bulletSection('Aos exames laboratoriais, apresenta:', textPieces))
  }

  return sections
}

function generateSpirometry({ answers }) {
  const textPieces = []

  if (!isEmpty(answers['40.a'])) {
    textPieces.push(`VEF1: ${answers['40.a']}% do previsto`)
  }

  if (!isEmpty(answers['40.b'])) {
    textPieces.push(`VEF1/CVF: ${answers['40.b']}`)
  }

  if (answers['40.c'] === 'sim') {
    textPieces.push(`Tem prova ao broncodilatador positiva`)
  }

  if (!isEmpty(answers['40.d'])) {
    textPieces.push(`Laudo: ${answers['40.d']}`)
  }

  const sections = []

  if (textPieces.length) {
    sections.push(bulletSection('Espirometria:', textPieces))
  }

  return sections
}

function generateFooter({ parameters, treatments, answers, extraAnswers }) {
  const textPieces = []

  const biologicTreatmentsMedicine = ['Mepolizumabe', 'Omalizumabe', 'Dupilumabe', 'Benralizumabe', 'Tezepelumabe']
  const indicatedTreatmentsId = new Set(Object.keys(extraAnswers['indicated-treatments'] ?? {}))
  const selectedBiologicTreatments = treatments
    .filter(
      (treatment) =>
        indicatedTreatmentsId.has(String(treatment.id)) &&
        biologicTreatmentsMedicine.some((medicine) => treatment.medicine.name.startsWith(medicine)),
    )
    .map((treatment) => treatment.medicine.name)

  if (!selectedBiologicTreatments.length) {
    return []
  }

  const firstPiece = [
    `Tendo em vista a caracterização como ${parameters['asthma:hypothesis']?.label.text.toLocaleLowerCase() ?? '-'}`,
    'refratária ao tratamento descrito',
    ...(!['0', 'sem-informacao'].includes(answers['9'])
      ? [`com ${answers['9'].replace('+', ' ou mais')} exacerbações no intervalo de um ano`]
      : []),
    `prescrevo ${selectedBiologicTreatments.join(', ')}, conforme receita em anexo.`,
  ].join(', ')

  textPieces.push(
    firstPiece,
    `O uso será por tempo indeterminado, enquanto o paciente estiver se beneficiando da terapia.`,
    'Ressalto que o objetivo em relação ao tratamento da asma aqui solicitado é atingir e manter  o controle da doença, que é definido como a intensidade com que as suas manifestações são suprimidas pelo tratamento.',
    'O controle da doença compreende dois objetivos distintos: Primeiro, o controle das limitações clínicas presentes, ou seja, sintomas mínimos durante o dia e ausência de sintomas à noite, pouca ou nenhuma necessidade de uso de medicamentos de alívio e ausência de limitação das atividades físicas; e, segundo, a redução dos riscos futuros, ou seja, das exacerbações, da perda da função pulmonar e dos efeitos adversos do tratamento. ',
    'Comprometo-me a realizar seguimento do caso em consultas regulares para orientar o paciente quanto ao seguimento correto do tratamento, monitorar sua adesão às medidas propostas e observar os efeitos positivos do medicamento assim como tratar com rapidez e responsabilidade os possíveis efeitos adversos associados à terapêutica proposta.',
    'Atenciosamente,',
  )

  return textPieces
}
